import type {
  IApiKey,
  IBulkDeleteApiKeysByUsersResponse,
  ICreateApiKeyBody,
  ICreateApiKeyResponse,
  IDeleteApiKeyProps,
  IGetApiKeyLogsByKeyParams,
  IGetApiKeyLogsParams,
  IGetApiKeyLogsResponse,
  IGetApiKeysProps,
  IGetOrganizationApiKeyLogsParams,
  IGetProjectApiKeyLogsParams,
  IGetUsersWithActiveKeysProps,
  IUpdateApiKeyScopesProps,
  IUpdateApiKeyScopesResponse,
  IUserWithActiveKeys,
} from "../types";

interface IBulkDeleteApiKeysByUsersApiProps {
  projectUuid: string;
  userUuids: string[];
}
import type { ApiClient } from "./client";

export class ApiKeyApi {
  constructor(private readonly apiClient: ApiClient) {}

  async getApiKeys({ projectUuid }: IGetApiKeysProps): Promise<IApiKey[]> {
    const response = await this.apiClient.get<IApiKey[]>(
      `/api-keys/project/${projectUuid}`,
    );
    return response.data;
  }

  async createApiKey(body: ICreateApiKeyBody): Promise<ICreateApiKeyResponse> {
    const response = await this.apiClient.post<ICreateApiKeyResponse>(
      "/api-keys",
      body,
    );
    return response.data;
  }

  // NOTE: NSAI-6881 — replaces the key's /v1 scopes; null restores full access.
  async updateApiKeyScopes({
    uuid,
    projectUuid,
    scopes,
  }: IUpdateApiKeyScopesProps): Promise<IUpdateApiKeyScopesResponse> {
    const response = await this.apiClient.patch<IUpdateApiKeyScopesResponse>(
      `/api-keys/${uuid}/scopes?projectUuid=${encodeURIComponent(projectUuid)}`,
      { scopes },
    );
    return response.data;
  }

  async deleteApiKey({ uuid, projectUuid }: IDeleteApiKeyProps): Promise<void> {
    await this.apiClient.post(
      `/api-keys/${uuid}/delete?projectUuid=${encodeURIComponent(projectUuid)}`,
    );
  }

  async getApiKeyLogs({
    limit,
    offset,
    projectUuid,
  }: IGetProjectApiKeyLogsParams): Promise<IGetApiKeyLogsResponse> {
    const params = new URLSearchParams();
    params.set("projectUuid", projectUuid);

    if (limit !== undefined) {
      params.set("limit", String(limit));
    }
    if (offset !== undefined) {
      params.set("offset", String(offset));
    }

    const query = params.toString();
    const path = query ? `/api-keys/logs?${query}` : "/api-keys/logs";

    const response = await this.apiClient.get<IGetApiKeyLogsResponse>(path);
    return response.data;
  }

  async getApiKeyLogsByKey({
    uuid,
    limit,
    offset,
    projectUuid,
  }: IGetApiKeyLogsByKeyParams): Promise<IGetApiKeyLogsResponse> {
    const params = new URLSearchParams();
    params.set("projectUuid", projectUuid);

    if (limit !== undefined) {
      params.set("limit", String(limit));
    }
    if (offset !== undefined) {
      params.set("offset", String(offset));
    }

    const query = params.toString();
    const basePath = `/api-keys/${uuid}/logs`;
    const path = query ? `${basePath}?${query}` : basePath;

    const response = await this.apiClient.get<IGetApiKeyLogsResponse>(path);
    return response.data;
  }

  async getApiKeysByUser(): Promise<IApiKey[]> {
    const response = await this.apiClient.get<IApiKey[]>("/api-keys/user");
    return response.data;
  }

  async getUserApiKeyLogs({
    limit,
    offset,
  }: IGetApiKeyLogsParams): Promise<IGetApiKeyLogsResponse> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.set("limit", String(limit));
    }
    if (offset !== undefined) {
      params.set("offset", String(offset));
    }
    const query = params.toString();
    const path = query ? `/api-keys/user/logs?${query}` : "/api-keys/user/logs";
    const response = await this.apiClient.get<IGetApiKeyLogsResponse>(path);
    return response.data;
  }

  async getOrganizationApiKeyLogs({
    organizationUuid,
    limit,
    offset,
  }: IGetOrganizationApiKeyLogsParams): Promise<IGetApiKeyLogsResponse> {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.set("limit", String(limit));
    }
    if (offset !== undefined) {
      params.set("offset", String(offset));
    }

    const query = params.toString();
    const basePath = `/api-keys/organization/${organizationUuid}/logs`;
    const path = query ? `${basePath}?${query}` : basePath;
    const response = await this.apiClient.get<IGetApiKeyLogsResponse>(path);
    return response.data;
  }

  async getUsersWithActiveKeys({
    projectUuid,
  }: IGetUsersWithActiveKeysProps): Promise<IUserWithActiveKeys[]> {
    const response = await this.apiClient.get<IUserWithActiveKeys[]>(
      `/api-keys/project/${projectUuid}/users`,
    );
    return response.data;
  }

  async bulkDeleteApiKeysByUsers({
    projectUuid,
    userUuids,
  }: IBulkDeleteApiKeysByUsersApiProps): Promise<IBulkDeleteApiKeysByUsersResponse> {
    const response =
      await this.apiClient.post<IBulkDeleteApiKeysByUsersResponse>(
        `/api-keys/project/${projectUuid}/users/delete`,
        { userUuids },
      );
    return response.data;
  }
}
